import {__} from '~/shared/src/utilities/messageCatalog';
import {SaveDataResult} from '~/shared/src/utilities/modules/data';

import {FORM_SAVE_DATA} from '~/frontend/src/constants';
import {
    StoreState,
    AppDispatch,
    Thunk
} from '~/frontend/src/store';
import {
    isDataModified,
    isDataModifiedRecursive
} from '~/frontend/src/utilities/dataUtils';
import {
    MsgParams,
    setMessage
} from '~/frontend/src/actions/intercom';
import dataSaveData from '~/frontend/src/actions/data/saveData';
import {
    setElusiveData,
    clearElusiveDataForSubpanels
} from '~/frontend/src/actions/elusiveData';
import deferedInputValueUpdate from '~/frontend/src/actions/panel/common/deferedInputValueUpdate';
import unsetParam from '~/frontend/src/actions/panel/common/unsetParam';


type Parameters = {
    [ key : string ] : any;
};


/**
 * @memberOf! Actions
 *
 * @param {string} panelKey
 * @param {string} dataContext
 * @param {Schema} schema
 * @param {number} rootItemId
 * @param {string} requestModule
 * @param {string} requestCommand
 * @param {Parameters} [requestParams]
 * @param {string[]} [viewDataContextsToSave]
 * @param {MsgParams} [successMsgParams]
 * @param {MsgParams} [nothingToSaveMsgParams]
 * @param {MsgParams} [errorMsgParams]
 *
 */
const saveData = ( panelKey : string, dataContext : string, schema : Schema, rootItemId : number, requestModule : string = 'data', requestCommand : string = 'save', requestParams? : Parameters, viewDataContextsToSave? : string[], successMsgParams? : MsgParams, nothingToSaveMsgParams? : MsgParams, errorMsgParams? : MsgParams ) : Thunk<Promise<SaveDataResult | false>> => async ( dispatch : AppDispatch, getState : () => StoreState ) : Promise<SaveDataResult | false> => {
    dispatch( {
        type: FORM_SAVE_DATA,
        panelKey,
        dataContext,
        schema,
        rootItemId,
        requestModule,
        requestCommand,
        requestParams,
        successMsgParams,
        nothingToSaveMsgParams,
        errorMsgParams
    } );

    await dispatch( deferedInputValueUpdate( panelKey, dataContext, schema ) );

    const requestUid       = 'saveData',
          formDataModified = rootItemId ? isDataModifiedRecursive( dataContext, schema, rootItemId ) : isDataModified( dataContext, schema );

    let retVal : SaveDataResult | false = false,
        formViewDataModified : boolean  = false;

    if ( viewDataContextsToSave?.length ) {
        for ( const viewDataContext of viewDataContextsToSave ) {
            const viewSchema = global.data[ viewDataContext ].schema;

            if ( viewSchema && isDataModified( viewDataContext, viewSchema ) ) {
                formViewDataModified = true;
                break;
            }
        }
    }

    // nothing to save
    if ( !Object.keys( requestParams ?? {} ).length && !formDataModified && !formViewDataModified ) {
        if ( !nothingToSaveMsgParams ) {
            nothingToSaveMsgParams = [
                panelKey,
                'toast',
                'nothingToSave',
                {
                    severity: 'info',
                    summary : __( 'YN.base.form.save.nothingToSave.title' ),
                    detail  : __( 'YN.base.form.save.nothingToSave.text' )
                }
            ];
        }

        dispatch( setMessage( ...nothingToSaveMsgParams ) );
    }

    // save request running
    else if ( getState().xhr?.[ panelKey ]?.[ requestUid ] ) {
        dispatch( setMessage(
            panelKey,
            'toast',
            'saveRunning',
            {
                severity: 'info',
                summary : __( 'YN.base.form.save.running.title' ),
                detail  : __( 'YN.base.form.save.running.text' )
            }
        ) );
    }

    // save
    else {
        /*dispatch( setMessage(
            panelKey,
            'toast',
            'save',
            {
                severity: 'info',
                summary : __( 'YN.base.form.save.saving.title' ),
                detail  : __( 'YN.base.form.save.saving.text' )
            }
        ) );*/

        let result : SaveDataResult | false = false;

        if ( Object.keys( requestParams ?? {} ).length || formDataModified || viewDataContextsToSave?.includes( dataContext ) ) {
            result = await dispatch( dataSaveData(
                dataContext,
                panelKey,
                requestUid,
                schema,
                `${requestModule}/${requestCommand}`,
                true,
                undefined,
                viewDataContextsToSave?.includes( dataContext )
                    ? undefined
                    : rootItemId,
                undefined,
                requestParams
            ) );
        }
        else {
            result = {
                success : true,
                data    : {
                    itemMetaDataMapping             : {},
                    propertyMetaDataMapping         : {},
                    referenceMetaDataMapping        : {},
                    referencePropertyMetaDataMapping: {}
                },
                metaData: {
                    metrics: []
                }
            };
        }

        retVal = result;

        // save success
        if ( retVal.success ) {
            if ( !retVal.data?.itemMetaDataMapping ) {
                retVal.data = {
                    ...retVal.data,
                    itemMetaDataMapping             : {},
                    propertyMetaDataMapping         : {},
                    referenceMetaDataMapping        : {},
                    referencePropertyMetaDataMapping: {}
                };
            }

            // save view data
            for ( const viewDataContext of viewDataContextsToSave ?? [] ) {
                if ( viewDataContext !== dataContext ) {
                    const viewSchema = global.data[ viewDataContext ].schema;

                    if ( viewSchema && isDataModified( viewDataContext, viewSchema ) ) {
                        await dispatch( dataSaveData(
                            viewDataContext,
                            panelKey,
                            requestUid,
                            viewSchema,
                            undefined,
                            true
                        ) );
                    }
                }
            }

            dispatch( unsetParam( panelKey, 'subPanelProps' ) );
            dispatch( clearElusiveDataForSubpanels( panelKey ) );
            dispatch( setElusiveData( panelKey, {
                ...getState().elusiveData?.[ panelKey ],
                variables                : {
                    ...getState().elusiveData?.[ panelKey ]?.variables,
                    lastSaveTimestamp: Date.now()
                },
                lastSaveRequestParameters: JSON.stringify( requestParams )
            } ) );

            if ( !successMsgParams ) {
                successMsgParams = [
                    panelKey,
                    'toast',
                    'saveSuccess',
                    {
                        severity: 'success',
                        summary : __( 'YN.base.form.save.saveSuccess.title' ),
                        detail  : __( 'YN.base.form.save.saveSuccess.text' )
                    }
                ];
            }

            dispatch( setMessage( ...successMsgParams ) );
        }

        // save error
        else if ( errorMsgParams ) {
            dispatch( setMessage( ...errorMsgParams ) );
        }
    }

    return retVal;
}
export type SaveData = typeof saveData;

export default saveData;
